// Turns an article + the editor form into (a) the data to PATCH and (b) the document to show in the live preview.
// Only "details" are editable here: status, tags, category, author, summary, SEO fields and picture addresses.
// The article's content blocks are never changed.
import { normalizeStatus } from '../api/contentApi';

export const META_TITLE_MAX = 60;
export const META_DESCRIPTION_MAX = 160;

export function parseTags(text) {
  return [...new Set(String(text || '').split(/[,;\n]/).map((t) => t.trim()).filter(Boolean))];
}

/** Picture blocks of the article, one per mediaId. */
export function imageBlocks(doc) {
  const seen = new Set();
  const out = [];
  for (const block of doc.content || []) {
    if (block.type !== 'image' || !block.mediaId || seen.has(block.mediaId)) continue;
    seen.add(block.mediaId);
    out.push({ mediaId: block.mediaId, caption: block.caption || '', alt: block.alt || '' });
  }
  return out;
}

export function isValidImageUrl(value) {
  const url = String(value || '').trim();
  return url === '' || /^https?:\/\/\S+$/i.test(url);
}

export function formFromArticle(entry, doc) {
  const media = doc.media || {};
  const featured = media[doc.meta?.featuredMediaId] || null;
  return {
    status: normalizeStatus(entry.status),
    tags: (doc.meta?.tags ?? entry.tags ?? []).join(', '),
    categoryName: doc.meta?.categoryName || entry.categoryName || '',
    authorName: doc.meta?.authorName || entry.authorName || '',
    excerpt: doc.meta?.excerpt ?? entry.excerpt ?? '',
    metaTitle: doc.seo?.metaTitle || doc.meta?.title || '',
    metaDescription: doc.seo?.metaDescription || '',
    focusKeyword: doc.seo?.focusKeyword || '',
    featuredImageUrl: entry.featuredImageUrl || featured?.url || '',
    featuredImageAlt: entry.featuredImageAlt || featured?.altText || '',
    pictures: Object.fromEntries(
      imageBlocks(doc).map((b) => [b.mediaId, { url: media[b.mediaId]?.url || '', alt: media[b.mediaId]?.altText ?? b.alt }]),
    ),
  };
}

/** Problems that stop saving, keyed by field. */
export function validateForm(entry, form) {
  const errors = {};
  if (!isValidImageUrl(form.featuredImageUrl)) errors.featuredImageUrl = 'Use a full web address starting with https://';
  for (const [id, p] of Object.entries(form.pictures)) {
    if (!isValidImageUrl(p.url)) errors[`picture:${id}`] = 'Use a full web address starting with https://';
  }
  if (form.status === 'published' && entry.valid === false) {
    errors.status = 'This article failed validation when it was imported and cannot be published.';
  }
  return errors;
}

/**
 * @returns {{doc, docData, rowData}}  doc = the document with the edits applied (for the live preview);
 * docData / rowData = what to PATCH into the content form / the index form.
 */
export function buildChanges(doc, form) {
  const tags = parseTags(form.tags);
  const category = form.categoryName.trim();
  const author = form.authorName.trim();

  // Picture addresses live in the document's `media` map: mediaId -> { url, altText }.
  const media = { ...(doc.media || {}) };
  const featuredUrl = form.featuredImageUrl.trim();
  if (featuredUrl) media.featured = { ...(media.featured || {}), url: featuredUrl, altText: form.featuredImageAlt.trim() };
  else delete media.featured;
  for (const [id, p] of Object.entries(form.pictures)) {
    const url = p.url.trim();
    if (url) media[id] = { ...(media[id] || {}), url, altText: p.alt.trim() };
    else delete media[id];
  }

  const keptId = doc.meta?.featuredMediaId && doc.meta.featuredMediaId !== 'featured' ? doc.meta.featuredMediaId : null;
  const featuredId = featuredUrl ? 'featured' : keptId && media[keptId]?.url ? keptId : null;

  const meta = {
    ...doc.meta,
    tags,
    categoryName: category || null,
    authorName: author || null,
    excerpt: form.excerpt.trim(),
    featuredMediaId: featuredId,
  };
  const seo = {
    ...doc.seo,
    metaTitle: form.metaTitle.trim(),
    metaDescription: form.metaDescription.trim(),
    focusKeyword: form.focusKeyword.trim() || null,
    ogImageMediaId: featuredId,
  };

  return {
    doc: { ...doc, meta, seo, media },
    docData: { meta, seo, media },
    rowData: {
      status: normalizeStatus(form.status),
      tags,
      categoryName: category,
      authorName: author,
      excerpt: meta.excerpt,
      featuredImageUrl: featuredId ? media[featuredId].url : '',
      featuredImageAlt: featuredId ? media[featuredId].altText || '' : '',
    },
  };
}
